import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorFormatterService } from '@core/services/error-formatter.service';
import { ComponentStore } from '@ngrx/component-store';
import { TranslateService } from '@ngx-translate/core';
import { CreatorPlatformListService } from '@pages/creators/creator-platform-list/creator-platform-list.service';
import { IChannel, IChannelCreate, ICheckChannelResponse } from '@pages/creators/creators.interface';
import { EPlatform } from '@shared/enums/campaign.enum';
import { camelCase, get } from 'lodash';
import { EMPTY, MonoTypeOperatorFunction, Observable, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

interface IAddPlatformInputState {
  createChannelLoading: boolean;
  createChannelSuccess: boolean;
  createdChannel: IChannel | null;
  error: string | null;
  errorCreatorExists: boolean;
  errorCreatorId: string | null;
  loading: boolean;
  validateChannelLoading: boolean;
  validatedChannelName: string | null;
  validatedChannelIcon: string | null;
  validatedChannelPlatform: string | null;
  validatedChannelResponse: ICheckChannelResponse | null;
  validPlatforms: EPlatform[] | null;
}

const initialState: IAddPlatformInputState = {
  createChannelLoading: false,
  createChannelSuccess: false,
  createdChannel: null,
  error: null,
  errorCreatorExists: false,
  errorCreatorId: null,
  loading: false,
  validateChannelLoading: false,
  validatedChannelIcon: null,
  validatedChannelName: null,
  validatedChannelPlatform: null,
  validatedChannelResponse: null,
  validPlatforms: null,
};

@Injectable()
export class AddPlatformInputStore extends ComponentStore<IAddPlatformInputState> {
  public readonly error$ = this.selectSignal((state: IAddPlatformInputState) => state.error);
  public readonly errorCreatorExists$ = this.selectSignal((state: IAddPlatformInputState) => state.errorCreatorExists);
  public readonly errorCreatorId$ = this.selectSignal((state: IAddPlatformInputState) => state.errorCreatorId);
  public readonly createChannelLoading$ = this.selectSignal(
    (state: IAddPlatformInputState) => state.createChannelLoading,
  );
  public readonly createChannelSuccess$ = this.selectSignal(
    (state: IAddPlatformInputState) => state.createChannelSuccess,
  );
  public readonly createdChannel$ = this.selectSignal((state: IAddPlatformInputState) => state.createdChannel);
  public readonly validateChannelLoading$ = this.selectSignal(
    (state: IAddPlatformInputState) => state.validateChannelLoading,
  );
  public readonly validatedChannelResponse$ = this.selectSignal(
    (state: IAddPlatformInputState) => state.validatedChannelResponse,
  );
  public readonly validatedChannelName$ = this.selectSignal(
    (state: IAddPlatformInputState) => state.validatedChannelName,
  );
  public readonly validatedChannelIcon$ = this.selectSignal(
    (state: IAddPlatformInputState) => state.validatedChannelIcon,
  );
  public readonly validatedChannelPlatform$ = this.selectSignal(
    (state: IAddPlatformInputState) => state.validatedChannelPlatform,
  );
  public readonly loading$ = this.selectSignal((state: IAddPlatformInputState) => state.loading);
  public readonly validPlatforms$ = this.selectSignal((state: IAddPlatformInputState) => state.validPlatforms);

  private readonly handleError = (): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError((error: HttpErrorResponse) => {
        const { message: errorMessage } = this.errorFormatterService.getErrorMessage(undefined, error.error);
        const errorDetails = this.errorFormatterService.getErrorDetails(error.error);
        const creatorName = errorDetails[0]?.params?.['creator_name'];
        const creatorId = (errorDetails[0]?.params?.['creator_id'] as string | null) ?? null;

        const detailsCode = errorDetails[0]?.code;
        const isChannelNotFoundOnPlatform = detailsCode === 'channel_not_found_on_platform';
        const resolvedError = isChannelNotFoundOnPlatform
          ? this.translate.instant('apiErrorMessages.channelNotFound')
          : get(error.error, 'error_code') === ErrorFormatterService.nonVisualErrorCode
            ? ''
            : errorMessage;

        this.patchState({
          createChannelLoading: false,
          createChannelSuccess: false,
          createdChannel: null,
          error: resolvedError,
          errorCreatorExists: !!creatorName && !!creatorId,
          errorCreatorId: creatorId,
          loading: false,
          validateChannelLoading: false,
        });

        if (isChannelNotFoundOnPlatform) {
          this.patchState({
            validatedChannelIcon: null,
            validatedChannelName: null,
            validatedChannelPlatform: null,
            validatedChannelResponse: null,
          });
        }

        return EMPTY;
      }),
    );

  public readonly createChannel = this.effect(
    (channel$: Observable<{ creatorId?: string; channel: IChannelCreate; creatorMode?: boolean }>) => {
      return channel$.pipe(
        tap(() => {
          this.patchState({
            createChannelLoading: true,
            createChannelSuccess: false,
            createdChannel: null,
            error: null,
            errorCreatorExists: false,
            errorCreatorId: null,
            loading: true,
          });
        }),
        switchMap(({ creatorId, channel, creatorMode }) => {
          const fetch$ = creatorMode
            ? this.service.addChannelCurrentCreator(channel)
            : this.service.addChannel(creatorId!, channel);

          return fetch$.pipe(
            tap((createdChannel) => {
              this.patchState({
                createChannelLoading: false,
                createChannelSuccess: true,
                createdChannel,
                loading: false,
              });
            }),
            this.handleError(),
          );
        }),
      );
    },
  );

  public readonly validateChannel = this.effect((channel$: Observable<IChannelCreate>) => {
    return channel$.pipe(
      tap(() => {
        this.patchState({
          error: null,
          errorCreatorExists: false,
          errorCreatorId: null,
          validateChannelLoading: true,
          validatedChannelName: null,
          validatedChannelResponse: null,
        });
      }),
      switchMap((channel) =>
        this.service.checkChannel(channel).pipe(
          tap((result: ICheckChannelResponse) => {
            let error: string | null = null;

            if (!result.platform) {
              error = this.translate.instant('apiErrorMessages.invalidChannelUrl');
            } else if (!result.channelId) {
              error = this.translate.instant('apiErrorMessages.invalidChannelId');
            } else if (!result.validChannel) {
              error =
                result.platform === 'INSTAGRAM'
                  ? this.translate.instant('apiErrorMessages.instagramNotBusinessOrNotFound')
                  : this.translate.instant('apiErrorMessages.channelNotFound');
            } else if (result.duplicatedChannel) {
              error = this.translate.instant('apiErrorMessages.alreadyExists.channel', {
                creatorName: result.duplicateCreatorName,
              });
            }

            const errorCreatorExists =
              !!result.duplicatedChannel && !!result.duplicateCreatorName && !!result.duplicateCreatorId;
            const errorCreatorId = result.duplicatedChannel ? (result.duplicateCreatorId ?? null) : null;

            this.patchState({
              error,
              errorCreatorExists,
              errorCreatorId,
              validateChannelLoading: false,
              validatedChannelIcon: error ? null : result.iconUrl || null,
              validatedChannelName: error ? null : result.channelId || null,
              validatedChannelPlatform: error ? null : result.platform || null,
              validatedChannelResponse: result,
            });
          }),
          this.handleError(),
        ),
      ),
    );
  });

  public readonly fetchValidPlatforms = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      switchMap(() =>
        this.service.getValidPlatforms().pipe(
          tap((platforms: string[]) => {
            this.patchState({
              validPlatforms: platforms.map((platform) => camelCase(platform) as EPlatform),
            });
          }),
          catchError(() => {
            this.patchState({ validPlatforms: [] });

            return EMPTY;
          }),
        ),
      ),
    );
  });

  constructor(
    private readonly service: CreatorPlatformListService,
    private readonly errorFormatterService: ErrorFormatterService,
    private readonly translate: TranslateService,
  ) {
    super(initialState);

    this.fetchValidPlatforms();
  }
}
